/**
 * ModuleConfigService - Иерархическая конфигурация модулей
 *
 * Приоритет конфигураций (от низшего к высшему):
 * 1. MF default - из /mf/{id}/config.json (публичный бакет)
 * 2. Org config - переопределение для организации
 * 3. Project config - переопределение для проекта
 * 4. Course config - переопределение для курса
 *
 * Backend API endpoints:
 * - GET /api/consultant/admin/modules - список всех манифестов
 * - GET /api/consultant/admin/modules/{id}/config?org=&project=&course= - merged config
 * - GET /api/consultant/admin/orgs/{org}/modules - список модулей организации
 * - PUT /api/consultant/admin/orgs/{org}/modules - сохранение списка модулей
 * - PUT /api/consultant/admin/orgs/{org}/mf/{id}/config - сохранение org-level config
 */

import { getApiClient } from './ApiClient'

// ============================================================================
// Types
// ============================================================================

/**
 * Манифест MF (читается из S3 публичного бакета через backend)
 */
export interface MFManifest {
  id: string
  title: LocalizedString
  description?: LocalizedString
  version: string
  entryPath: string
  stylePath?: string
  buildHash?: string
  menu?: {
    section: 'tools' | 'admin' | 'settings' | 'main'
    order: number
    icon: string
  }
  widgets?: Array<{
    id: string
    title: LocalizedString
    mountFunction: string
    position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'custom'
    autoMount?: boolean
  }>
  routes?: Array<{
    path: string
    title: LocalizedString
    icon?: string
    requiresAuth?: boolean
  }>
  requiresAuth: boolean
  permissions?: string[]
}

export interface LocalizedString {
  ru: string
  en?: string
  [key: string]: string | undefined
}

/**
 * Элемент списка модулей организации
 */
export interface OrgModuleEntry {
  /** ID модуля (ссылка на манифест) */
  id: string
  /** Порядок в меню/списке */
  order: number
  /** Включён для этой организации */
  enabled: boolean
}

/**
 * Список модулей организации
 */
export interface OrgModulesList {
  modules: OrgModuleEntry[]
  updatedAt: string
  updatedBy?: string
}

/**
 * Merged config от backend API
 */
export interface MergedModuleConfig {
  id: string
  settings: Record<string, unknown>
  sources: {
    mfDefault: boolean
    org: boolean
    project: boolean
    course: boolean
  }
}

/**
 * Конфигурация одного модуля (legacy - для обратной совместимости)
 */
export interface ModuleConfig {
  /** ID модуля */
  id: string
  /** Статус модуля */
  status: 'active' | 'inactive'
  /** Время включения */
  enabledAt?: string
  /** Время отключения */
  disabledAt?: string
  /** Дополнительные настройки модуля */
  settings?: Record<string, unknown>
}

/**
 * Контейнер конфигураций модулей (legacy)
 */
export interface ModulesConfig {
  /** Конфигурации модулей по ID */
  modules: Record<string, ModuleConfig>
  /** Время последнего обновления */
  updatedAt: string
  /** Кем обновлено */
  updatedBy?: string
}

/**
 * Контекст для получения merged config
 */
export interface ConfigContext {
  orgSlug?: string
  projectId?: string
  courseId?: string
}

// ============================================================================
// Service
// ============================================================================

class ModuleConfigServiceImpl {
  private apiClient = getApiClient({
    baseUrl: import.meta.env.VITE_API_URL || '/api/consultant',
  })

  // Cache
  private manifestsCache: MFManifest[] | null = null
  private orgModulesCache: Map<string, OrgModulesList> = new Map()
  private mergedConfigCache: Map<string, MergedModuleConfig> = new Map()

  // Legacy cache
  private portalConfig: ModulesConfig | null = null
  private orgConfigs: Map<string, ModulesConfig> = new Map()

  // ==========================================================================
  // New API: Manifests
  // ==========================================================================

  /**
   * Загрузить список всех манифестов MF
   */
  async loadAllManifests(): Promise<MFManifest[]> {
    if (this.manifestsCache) {
      return this.manifestsCache
    }

    try {
      const manifests = await this.apiClient.get<MFManifest[]>('/admin/modules')
      this.manifestsCache = manifests
      return manifests
    } catch (error) {
      console.warn('[ModuleConfig] Failed to load manifests:', error)
      return []
    }
  }

  /**
   * Получить манифест по ID
   */
  async getManifest(moduleId: string): Promise<MFManifest | null> {
    const manifests = await this.loadAllManifests()
    return manifests.find((m) => m.id === moduleId) || null
  }

  // ==========================================================================
  // New API: Org Modules List
  // ==========================================================================

  /**
   * Загрузить список модулей организации
   */
  async loadOrgModules(orgSlug: string): Promise<OrgModulesList> {
    const cached = this.orgModulesCache.get(orgSlug)
    if (cached) {
      return cached
    }

    try {
      const modulesList = await this.apiClient.get<OrgModulesList>(`/admin/orgs/${orgSlug}/modules`)
      this.orgModulesCache.set(orgSlug, modulesList)
      return modulesList
    } catch (error) {
      console.debug('[ModuleConfig] No org modules list for', orgSlug)
      return {
        modules: [],
        updatedAt: new Date().toISOString(),
      }
    }
  }

  /**
   * Сохранить список модулей организации
   */
  async saveOrgModules(orgSlug: string, modules: OrgModuleEntry[]): Promise<OrgModulesList> {
    const modulesList: OrgModulesList = {
      modules,
      updatedAt: new Date().toISOString(),
    }

    const result = await this.apiClient.put<OrgModulesList>(
      `/admin/orgs/${orgSlug}/modules`,
      modulesList
    )

    this.orgModulesCache.set(orgSlug, result)
    return result
  }

  /**
   * Получить отфильтрованные и отсортированные манифесты для организации
   */
  async getOrgManifests(orgSlug: string): Promise<MFManifest[]> {
    const [allManifests, orgModules] = await Promise.all([
      this.loadAllManifests(),
      this.loadOrgModules(orgSlug),
    ])

    // Если у организации нет списка модулей - возвращаем все
    if (orgModules.modules.length === 0) {
      return allManifests
    }

    // Фильтруем и сортируем по order
    const enabledIds = new Set(orgModules.modules.filter((m) => m.enabled).map((m) => m.id))

    const orderMap = new Map(orgModules.modules.map((m) => [m.id, m.order]))

    return allManifests
      .filter((m) => enabledIds.has(m.id))
      .sort((a, b) => {
        const orderA = orderMap.get(a.id) ?? 999
        const orderB = orderMap.get(b.id) ?? 999
        return orderA - orderB
      })
  }

  // ==========================================================================
  // New API: Merged Config (full hierarchy)
  // ==========================================================================

  /**
   * Получить merged config для модуля с полной иерархией
   * mf default → org → project → course
   */
  async getMergedConfig(
    moduleId: string,
    context: ConfigContext = {}
  ): Promise<MergedModuleConfig> {
    const cacheKey = this.buildCacheKey(moduleId, context)
    const cached = this.mergedConfigCache.get(cacheKey)
    if (cached) {
      return cached
    }

    try {
      // Строим query params
      const params = new URLSearchParams()
      if (context.orgSlug) params.set('org', context.orgSlug)
      if (context.projectId) params.set('project', context.projectId)
      if (context.courseId) params.set('course', context.courseId)

      const queryString = params.toString()
      const url = `/admin/modules/${moduleId}/config${queryString ? `?${queryString}` : ''}`

      const config = await this.apiClient.get<MergedModuleConfig>(url)
      this.mergedConfigCache.set(cacheKey, config)
      return config
    } catch (error) {
      console.warn('[ModuleConfig] Failed to load merged config for', moduleId, error)
      return {
        id: moduleId,
        settings: {},
        sources: {
          mfDefault: false,
          org: false,
          project: false,
          course: false,
        },
      }
    }
  }

  /**
   * Сохранить org-level config для модуля
   */
  async saveOrgModuleConfig(
    orgSlug: string,
    moduleId: string,
    settings: Record<string, unknown>
  ): Promise<void> {
    await this.apiClient.put(`/admin/orgs/${orgSlug}/mf/${moduleId}/config`, { settings })

    // Инвалидируем кэш для этого модуля
    this.invalidateMergedConfigCache(moduleId)
  }

  private buildCacheKey(moduleId: string, context: ConfigContext): string {
    const parts = [moduleId]
    if (context.orgSlug) parts.push(`org:${context.orgSlug}`)
    if (context.projectId) parts.push(`proj:${context.projectId}`)
    if (context.courseId) parts.push(`course:${context.courseId}`)
    return parts.join('|')
  }

  private invalidateMergedConfigCache(moduleId: string): void {
    // Удаляем все записи кэша для этого модуля
    for (const key of this.mergedConfigCache.keys()) {
      if (key.startsWith(moduleId)) {
        this.mergedConfigCache.delete(key)
      }
    }
  }

  // ==========================================================================
  // Legacy API (для обратной совместимости)
  // ==========================================================================

  /**
   * Загрузить конфигурацию портала (legacy)
   */
  async loadPortalConfig(): Promise<ModulesConfig> {
    if (this.portalConfig) {
      return this.portalConfig
    }

    try {
      const config = await this.apiClient.get<ModulesConfig>('/admin/modules/config')
      this.portalConfig = config
      return config
    } catch (error) {
      console.warn('[ModuleConfig] Failed to load portal config, using defaults:', error)
      return {
        modules: {},
        updatedAt: new Date().toISOString(),
      }
    }
  }

  /**
   * Загрузить конфигурацию организации (legacy)
   */
  async loadOrgConfig(orgSlug: string): Promise<ModulesConfig> {
    const cached = this.orgConfigs.get(orgSlug)
    if (cached) {
      return cached
    }

    try {
      const config = await this.apiClient.get<ModulesConfig>(
        `/admin/orgs/${orgSlug}/modules/config`
      )
      this.orgConfigs.set(orgSlug, config)
      return config
    } catch (error) {
      // Организация может не иметь своей конфигурации - это нормально
      console.debug('[ModuleConfig] No org config for', orgSlug)
      return {
        modules: {},
        updatedAt: new Date().toISOString(),
      }
    }
  }

  /**
   * Получить merged конфигурацию для модуля (legacy - portal + org only)
   */
  getLegacyMergedConfig(
    moduleId: string,
    defaultStatus: 'active' | 'inactive' = 'active',
    orgSlug?: string
  ): {
    id: string
    status: 'active' | 'inactive'
    statusSource: 'default' | 'portal' | 'org'
    settings: Record<string, unknown>
    defaultStatus: 'active' | 'inactive'
    portalStatus?: 'active' | 'inactive'
    orgStatus?: 'active' | 'inactive'
  } {
    const portalModuleConfig = this.portalConfig?.modules[moduleId]
    const orgModuleConfig = orgSlug ? this.orgConfigs.get(orgSlug)?.modules[moduleId] : undefined

    // Определяем итоговый статус и источник
    let status: 'active' | 'inactive' = defaultStatus
    let statusSource: 'default' | 'portal' | 'org' = 'default'

    // Портальная конфигурация переопределяет default
    if (portalModuleConfig?.status) {
      status = portalModuleConfig.status
      statusSource = 'portal'
    }

    // Конфигурация организации переопределяет портальную
    if (orgModuleConfig?.status) {
      status = orgModuleConfig.status
      statusSource = 'org'
    }

    // Мержим настройки
    const settings: Record<string, unknown> = {
      ...(portalModuleConfig?.settings || {}),
      ...(orgModuleConfig?.settings || {}),
    }

    return {
      id: moduleId,
      status,
      statusSource,
      settings,
      defaultStatus,
      portalStatus: portalModuleConfig?.status,
      orgStatus: orgModuleConfig?.status,
    }
  }

  /**
   * Проверить, включен ли модуль (legacy)
   */
  isModuleEnabled(
    moduleId: string,
    defaultStatus: 'active' | 'inactive' = 'active',
    orgSlug?: string
  ): boolean {
    const config = this.getLegacyMergedConfig(moduleId, defaultStatus, orgSlug)
    return config.status === 'active'
  }

  /**
   * Инвалидировать кэш
   */
  invalidateCache(orgSlug?: string) {
    if (orgSlug) {
      this.orgConfigs.delete(orgSlug)
      this.orgModulesCache.delete(orgSlug)
    } else {
      this.portalConfig = null
      this.orgConfigs.clear()
      this.manifestsCache = null
      this.orgModulesCache.clear()
      this.mergedConfigCache.clear()
    }
  }

  /**
   * Загрузить все конфигурации (portal + org) - legacy
   */
  async loadAll(orgSlug?: string): Promise<void> {
    await this.loadPortalConfig()
    if (orgSlug) {
      await this.loadOrgConfig(orgSlug)
    }
  }
}

// Singleton
export const moduleConfigService = new ModuleConfigServiceImpl()
export default moduleConfigService
